import React, { useState } from 'react'
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { LeagueItem, PartnerPageData } from 'types/types'
import { getLeagueColor } from '@/utils/getLeagueColor'
import { isTBLPage } from '@/utils/tblUtils'

const THE_BIG_LEAGUES_IMAGE_PATH = '/partner/the-big-leagues/logo-square.png'

function LeagueAvatar({
  league,
  size,
  page,
}: {
  league: LeagueItem | null
  size: number
  page: PartnerPageData
}) {
  if (league) {
    return league.logo ? (
      <Box
        component="img"
        src={league.logo}
        alt={league.name}
        width={size}
        height={size}
        borderRadius="50%"
        sx={{ objectFit: 'cover', flexShrink: 0 }}
      />
    ) : (
      <Box
        width={size}
        height={size}
        borderRadius="50%"
        sx={{ backgroundColor: getLeagueColor(league.id), flexShrink: 0 }}
      />
    )
  }

  // No league selected: the "Global" entry falls back to the page's own branding.
  if (isTBLPage(page)) {
    return (
      <Box
        component="img"
        src={THE_BIG_LEAGUES_IMAGE_PATH}
        alt="The Big Leagues"
        width={size}
        height={size}
        borderRadius="50%"
        sx={{
          objectFit: 'contain',
          flexShrink: 0,
          backgroundColor: '#000',
          padding: '2px',
          boxSizing: 'border-box',
        }}
      />
    )
  }

  return page.logo ? (
    <Box
      component="img"
      src={page.logo}
      alt="Global"
      width={size}
      height={size}
      borderRadius="50%"
      sx={{ objectFit: 'cover', flexShrink: 0 }}
    />
  ) : (
    <Box
      width={size}
      height={size}
      borderRadius="50%"
      sx={{ backgroundColor: '#9E9E9E', flexShrink: 0 }}
    />
  )
}

export default function DropdownLeaguePicker({
  options,
  value,
  onChange,
  page,
}: {
  options: LeagueItem[]
  value: number
  onChange: (val: number) => void
  page: PartnerPageData
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // `value` is the index of the selected league in `options`, or -1 for "Global".
  const selected = value >= 0 ? options[value] ?? null : null

  return (
    <>
      {/* Trigger */}
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          borderRadius: '999px',
          px: 2,
          py: 1,
          textTransform: 'none',
          backgroundColor: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: '#fff',
          backdropFilter: 'blur(6px)',
          gap: 1,
        }}
      >
        <LeagueAvatar league={selected} size={18} page={page} />

        <Typography fontSize={12} fontWeight={600}>
          {selected?.name || 'Global'}
        </Typography>
      </Button>

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          key={-1}
          onClick={() => {
            onChange(-1)
            setAnchorEl(null)
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <LeagueAvatar league={null} size={22} page={page} />
          <Typography fontSize={14}>Global</Typography>
        </MenuItem>
        {options.map((option, ind) => (
          <MenuItem
            key={option.id}
            onClick={() => {
              onChange(ind)
              setAnchorEl(null)
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <LeagueAvatar league={option} size={22} page={page} />
            <Typography fontSize={14}>{option.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
